import { StudioSystemType } from 'types/global'

import { FIXED_INCENTIVE, QuoteAdderRow, QuoteIncentiveRow } from '../lineItems/types'

export type QuoteLineItemsPayload = {
  line_items: QuoteAdderRow[]
  incentives: QuoteIncentiveRow[]
}

/** Latest GET /projects/{id}/ payload cached so Quote widgets can read OsSystem userData when `loadScene` fails or lags. */
let snapshotByProjectId = new Map<string, unknown>()

const normUuidLocal = (u: string | null | undefined) => (u || '').toLowerCase()

const generateStudioUuid = (): string => {
  const w = window as any
  if (w.THREE?.Math?.generateUUID) {
    return w.THREE.Math.generateUUID()
  }
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return `quote-scene-${Date.now()}`
}

/**
 * Quote placeholder designs from the API are often ``{ object: { children: [OsSystem] }, version }``
 * without the top-level ``metadata.type`` Studio's ``Loader.handleJSON`` expects. When ``type`` is
 * missing it defaults to legacy ``Geometry`` and calls ``THREE.JSONLoader`` (removed in current
 * Three.js) — which is the ``loadScene failed: JSONLoader is not a constructor`` error on Payments.
 *
 * Wrap bare roots in ``OsDesignerScene`` and set ``metadata.type`` to ``Object`` so
 * ``ObjectLoader`` parses the scene like a normal solar project design.
 */
export const normalizeDesignJsonForStudioLoad = (designJson: any): any => {
  if (!designJson || typeof designJson !== 'object') return designJson

  const normalized: Record<string, unknown> = { ...designJson }
  const existingMeta =
    normalized.metadata && typeof normalized.metadata === 'object'
      ? (normalized.metadata as Record<string, unknown>)
      : {}

  const metaType = typeof existingMeta.type === 'string' ? existingMeta.type.toLowerCase() : ''
  // Studio Loader defaults missing metadata to legacy Geometry → JSONLoader (removed in current Three).
  if (!metaType || metaType === 'geometry' || metaType === 'buffergeometry') {
    normalized.metadata = {
      ...existingMeta,
      type: 'Object',
    }
  }

  const objectRoot = normalized.object
  if (!objectRoot || typeof objectRoot !== 'object') return normalized

  const root = objectRoot as Record<string, unknown>
  const children = root.children
  const isBareChildrenRoot =
    root.type !== 'OsDesignerScene' &&
    Array.isArray(children) &&
    children.some((c: any) => c?.type === 'OsSystem')

  if (isBareChildrenRoot) {
    const sceneUserData =
      root.userData && typeof root.userData === 'object'
        ? (root.userData as Record<string, unknown>)
        : { views: [], historyData: {} }

    normalized.object = {
      uuid: root.uuid ?? generateStudioUuid(),
      type: 'OsDesignerScene',
      name: 'OsDesignerScene',
      userData: sceneUserData,
      children,
    }
  }

  return normalized
}

/**
 * When ``loadScene`` cannot run (legacy design shape, JSONLoader errors, etc.), attach the
 * placeholder ``OsSystem`` from the server design onto the live editor scene so Payments can read
 * ``window.editor.getSystems()``.
 */
export const injectQuoteOsSystemIntoEditor = (
  projectData: unknown,
  systemUuid: string
): boolean => {
  const w = window as any
  const editor = w.editor
  if (!editor?.execute || !w.OsSystem || !w.AddObjectCommand) return false

  const designJson = parseDesignFromProjectPayload(projectData)
  const stub = designJson ? extractQuoteStudioSystemStubFromDesign(designJson, systemUuid) : null
  const userData = stub?.userData
  if (!userData || typeof userData !== 'object') return false

  const target = normUuidLocal(systemUuid)
  const existing = editor.getSystems?.()?.find((s: any) => normUuidLocal(s?.uuid) === target)
  if (existing) {
    applyFreshUserDataToStudioSystem(projectData, systemUuid)
    return true
  }

  const scene = editor.scene
  if (!scene) return false

  const wasLoading = !!editor.sceneIsLoading
  editor.sceneIsLoading = true
  try {
    const newSystem = new w.OsSystem({
      fromJSON: true,
      userData,
      uuid: (stub as any)?.uuid ?? systemUuid,
    })
    editor.execute(new w.AddObjectCommand(newSystem, scene, true))
    if (!wasLoading) {
      editor.sceneIsLoading = false
    }
    try {
      editor.signals?.objectChanged?.dispatch?.(newSystem)
      editor.signals?.sceneLoaded?.dispatch?.()
    } catch {
      /* non-fatal */
    }
    return true
  } catch (e) {
    console.warn('[injectQuoteOsSystemIntoEditor] failed', e)
    if (!wasLoading) {
      editor.sceneIsLoading = false
    }
    return false
  }
}

/**
 * Apply the freshly-fetched project payload's `userData` onto Studio's in-memory `OsSystem`
 * for the placeholder Quote system, then dispatch `editor.signals.objectChanged` so reactive
 * widgets (`useQuoteStudioSystem`, the cashflow milestones table, etc.) re-read the new
 * values without requiring a full page reload.
 *
 * Why this is needed: the Quote page's right-hand widgets read live data from
 * `window.editor.getSystems()` (Studio's Three.js scene), not from the cached project
 * snapshot. After a successful project design PATCH (Studio save) or live preview,
 * on the backend, the snapshot is fresh but Studio's `OsSystem` still holds the pre-save
 * `payment_options` / `pricing` / `line_items`, so the UI keeps showing stale prices until
 * the user hard-refreshes the browser. Re-running `editor.loadScene` would work but it
 * tears down the whole Three.js scene; this helper just rebinds the userData on the
 * matching system, which is enough for the read-only Quote page (no module / inverter /
 * battery edits happen here).
 *
 * No-op when Studio isn't ready, when the system isn't in the editor yet, or when the fresh
 * payload doesn't contain a matching `OsSystem` node — callers can dispatch
 * `triggerSystemRefresh` regardless, since `useQuoteStudioSystem` falls back to the design
 * snapshot stub when Studio is absent.
 */
export const applyFreshUserDataToStudioSystem = (
  projectData: unknown,
  systemUuid: string | null | undefined
): void => {
  if (!systemUuid) return
  const designJson = parseDesignFromProjectPayload(projectData)
  const children = designJson?.object?.children
  if (!Array.isArray(children)) return
  const freshNode = children.find(
    (c: any) => c?.type === 'OsSystem' && normUuidLocal(c?.uuid) === normUuidLocal(systemUuid)
  )
  const freshUserData = freshNode?.userData
  if (!freshUserData || typeof freshUserData !== 'object') return

  const editor: any = (window as any).editor
  const studioSystem = editor?.getSystems?.()?.find(
    (s: any) => normUuidLocal(s?.uuid) === normUuidLocal(systemUuid)
  )
  if (!studioSystem) return

  // Replace userData wholesale so a key that disappeared on the server (e.g. an override that
  // was cleared) is also dropped from Studio's view. `applyUserData` walks the field list and
  // mirrors each key into the corresponding `OsSystem` property, then `objectChanged` makes
  // every reactive consumer re-read.
  studioSystem.userData = freshUserData
  if (typeof studioSystem.applyUserData === 'function') {
    try {
      studioSystem.applyUserData()
    } catch (e) {
      console.warn('[applyFreshUserDataToStudioSystem] applyUserData failed', e)
    }
  }
  try {
    editor?.signals?.objectChanged?.dispatch?.(studioSystem)
  } catch {
    /* swallow — signal dispatch errors are not fatal for this read-only refresh path */
  }
}

/**
 * Patch a subset of `userData` keys onto Studio's in-memory `OsSystem` for the placeholder
 * Quote system. Mirror of `applyFreshUserDataToStudioSystem` for the **live preview** path
 * (`quote_line_items_preview`), which returns only the fields the Quote page renders
 * (`pricing`, `payment_options`, `line_items`, mirrored incentives) rather than a full
 * server-side userData. We merge the patch onto whatever Studio currently holds so the
 * unrelated keys (`uuid`, `type`, `id`, `payment_options_override`, etc.) survive — only
 * the pricing-relevant rows change.
 *
 * Unlike the wholesale-replace helper above, this is meant to be invoked frequently (1.5 s
 * debounce after each edit) and against unsaved data, so we make a defensive copy of the
 * userData dict before mutating to avoid trampling any concurrent reads.
 *
 * No-op when Studio isn't ready, when the system isn't in the editor yet, or when the patch
 * is empty / missing.
 */
export const patchPreviewUserDataIntoStudioSystem = (
  patch: Record<string, unknown> | null | undefined,
  systemUuid: string | null | undefined
): void => {
  if (!systemUuid) return
  if (!patch || typeof patch !== 'object') return
  const patchKeys = Object.keys(patch)
  if (patchKeys.length === 0) return

  const editor: any = (window as any).editor
  const studioSystem = editor?.getSystems?.()?.find(
    (s: any) => normUuidLocal(s?.uuid) === normUuidLocal(systemUuid)
  )
  if (!studioSystem) return

  const previous = (studioSystem.userData && typeof studioSystem.userData === 'object'
    ? studioSystem.userData
    : {}) as Record<string, unknown>
  // Shallow-merge: preview only ever replaces top-level keys (e.g. swap the whole `pricing`
  // dict, the whole `payment_options` array). Anything not in `patch` is left untouched so
  // an unrelated key written elsewhere (e.g. `payment_options_override` set via
  // ``SetValueCommand`` before Save) is not clobbered by a stale preview response.
  studioSystem.userData = { ...previous, ...patch }
  if (typeof studioSystem.applyUserData === 'function') {
    try {
      studioSystem.applyUserData()
    } catch (e) {
      console.warn('[patchPreviewUserDataIntoStudioSystem] applyUserData failed', e)
    }
  }
  try {
    editor?.signals?.objectChanged?.dispatch?.(studioSystem)
  } catch {
    /* swallow — signal dispatch errors are not fatal for this read-only preview path */
  }
}

/**
 * Apply a live-preview patch to the cached project snapshot as well as Studio. Some non-solar
 * Quote widgets render from this snapshot fallback when the placeholder OsSystem hasn't been
 * hydrated into `window.editor.getSystems()` yet; without updating it, preview calcs can succeed
 * but the right column still shows stale or empty totals until a hard reload.
 */
export const patchPreviewUserDataIntoQuoteProjectSnapshot = (
  projectId: string | number | null | undefined,
  patch: Record<string, unknown> | null | undefined,
  systemUuid: string | null | undefined
): void => {
  if (projectId == null || projectId === 'new') return
  if (!systemUuid || !patch || typeof patch !== 'object') return
  const projectData = getQuoteProjectSnapshot(projectId)
  if (!projectData || typeof projectData !== 'object') return
  const designJson = parseDesignFromProjectPayload(projectData)
  const children = designJson?.object?.children
  if (!Array.isArray(children)) return
  const child = children.find(
    (c: any) => c?.type === 'OsSystem' && normUuidLocal(c?.uuid) === normUuidLocal(systemUuid)
  )
  if (!child) return
  const previous = child.userData && typeof child.userData === 'object' ? child.userData : {}
  child.userData = { ...previous, ...patch }
  ;(projectData as { design?: unknown }).design = designJson
  setQuoteProjectSnapshot(projectId, projectData)
}

/** Bump so hooks can subscribe if we ever need explicit version; currently Redux refresh covers it. */
export const setQuoteProjectSnapshot = (projectId: string | number, projectData: unknown) => {
  snapshotByProjectId.set(String(projectId), projectData)
}

const readQuoteLineItemsFromUserData = (userData: Record<string, unknown>): QuoteLineItemsPayload => {
  const line_items: QuoteAdderRow[] = []
  const rawLineItems = userData.line_items
  if (Array.isArray(rawLineItems)) {
    for (const row of rawLineItems) {
      if (!row || typeof row !== 'object') continue
      const r = row as Record<string, unknown>
      const label = typeof r.label === 'string' ? r.label.trim() : ''
      const adderId = r.adder_id
      const hasLibraryRef = typeof adderId === 'number' && adderId > 0
      if (!label && !hasLibraryRef) continue
      line_items.push(row as QuoteAdderRow)
    }
  }

  const incentives: QuoteIncentiveRow[] = []
  const rawInstaller = userData.incentive_to_installer
  if (Array.isArray(rawInstaller)) {
    for (const row of rawInstaller) {
      if (!row || typeof row !== 'object') continue
      const r = row as Record<string, unknown>
      const title = typeof r.title === 'string' ? r.title.trim() : ''
      const value = typeof r.value === 'number' ? r.value : null
      if (!title && value == null) continue
      incentives.push({
        incentive_id: null,
        incentive_type: FIXED_INCENTIVE,
        title,
        inc_tax: value,
        settings: { value, price_based_incentive_type: 'price_including_tax' },
      })
    }
  }

  return { line_items, incentives }
}

/** Read persisted Quote rows from ``userData.line_items`` / ``incentive_to_installer``. */
export const getQuoteLineItemsFromProjectPayload = (
  projectData: unknown,
  systemUuid: string
): QuoteLineItemsPayload => {
  const designJson = parseDesignFromProjectPayload(projectData)
  if (!designJson) return { line_items: [], incentives: [] }

  const children = designJson?.object?.children
  if (!Array.isArray(children)) return { line_items: [], incentives: [] }

  const child = children.find(
    (c: any) => c?.type === 'OsSystem' && normUuidLocal(c?.uuid) === normUuidLocal(systemUuid)
  )
  const ud = child?.userData
  if (!ud || typeof ud !== 'object') return { line_items: [], incentives: [] }

  return readQuoteLineItemsFromUserData(ud as Record<string, unknown>)
}

/**
 * Merge rows from ``ensure_quote_system`` with a cached project-design read.
 *
 * The ensure response always includes ``line_items`` / ``incentives`` arrays read from
 * the DB (possibly empty after the user deleted every row). Do **not** treat
 * ``length === 0`` as "missing" and substitute snapshot rows — that resurrected
 * deleted adders on reload when the in-memory project snapshot was stale.
 */
export const mergeQuoteLineItemsPayloads = (
  fromEnsure: QuoteLineItemsPayload,
  fromDesign: QuoteLineItemsPayload
): QuoteLineItemsPayload => ({
  line_items: fromEnsure.line_items ?? fromDesign.line_items,
  incentives: fromEnsure.incentives ?? fromDesign.incentives,
})

export const getQuoteProjectSnapshot = (projectId: string | number | null | undefined): unknown | null => {
  if (projectId == null || projectId === 'new') return null
  return snapshotByProjectId.get(String(projectId)) ?? null
}

/**
 * Turn API `design` (compressed string or plain object) into the deserialized scene JSON Studio
 * consumes, using the same helpers as `projectSections/index.tsx` when available.
 */
export const parseDesignFromProjectPayload = (projectData: unknown): any => {
  if (!projectData || typeof projectData !== 'object') return null
  const payload = projectData as { design?: unknown; proposal_data?: { design?: unknown } }
  // Customer `myenergy` payloads often expose design only under `proposal_data.design`.
  const raw = payload.design ?? payload.proposal_data?.design
  if (raw == null) return null
  const w = window as any
  try {
    if (w.editor?.deserializeDesign) {
      return w.editor.deserializeDesign(raw)
    }
    if (w.CompressionHelper?.decompress) {
      return w.CompressionHelper.decompress(raw, true)
    }
    return typeof raw === 'object' ? raw : null
  } catch {
    return null
  }
}

const normUuid = (u: string | null | undefined) => (u || '').toLowerCase()

/**
 * Build a **plain object** shaped enough like `StudioSystemType` for read-only Quote widgets when
 * the live Three.js `OsSystem` is not in `editor.getSystems()` yet (e.g. `loadScene` failed).
 *
 * This is intentionally narrow — do not pass to flows that execute Studio commands unless the
 * real runtime object exists.
 */
/**
 * Prefer the source that already has computed ``payment_options`` / pricing. The live
 * Three.js ``OsSystem`` from project bootstrap often lags behind ``project.design`` after
 * ``ensure_quote_system`` or a quote API write — using ``fromEditor ?? fromDesign`` alone
 * leaves Payment Setup / Quote Summary stuck on "Pricing is still calculating…" until a
 * full ``loadScene``, even though the server design already has options.
 */
export const pickFresherQuoteStudioSystem = (
  fromEditor: StudioSystemType | null,
  fromDesign: StudioSystemType | null
): StudioSystemType | null => {
  if (!fromEditor) return fromDesign
  if (!fromDesign) return fromEditor

  const editorOptionCount = fromEditor.payment_options?.length ?? 0
  const designOptionCount = fromDesign.payment_options?.length ?? 0
  if (designOptionCount > editorOptionCount) return fromDesign

  const editorSubtotal = (fromEditor as { pricing?: { system_price_excluding_tax?: number } }).pricing
    ?.system_price_excluding_tax
  const designSubtotal = (fromDesign as { pricing?: { system_price_excluding_tax?: number } }).pricing
    ?.system_price_excluding_tax
  const editorLooksUnpriced =
    editorOptionCount === 0 &&
    (editorSubtotal == null || Number(editorSubtotal) === 0)
  const designLooksPriced =
    designOptionCount > 0 ||
    (designSubtotal != null && Math.abs(Number(designSubtotal)) > 0.001)
  if (editorLooksUnpriced && designLooksPriced) return fromDesign

  return fromEditor
}

export const extractQuoteStudioSystemStubFromDesign = (
  designJson: any,
  systemUuid: string
): StudioSystemType | null => {
  const children = designJson?.object?.children
  if (!Array.isArray(children)) return null
  const child = children.find(
    (c: any) => c?.type === 'OsSystem' && normUuid(c?.uuid) === normUuid(systemUuid)
  )
  if (!child?.userData || typeof child.userData !== 'object') return null
  const ud = child.userData as Record<string, unknown>

  const stub = {
    uuid: child.uuid,
    userData: ud,
    payment_options: (ud.payment_options as StudioSystemType['payment_options']) ?? [],
    line_items: (ud.line_items as StudioSystemType['line_items']) ?? [],
    ...ud,
  }
  // Read-only stub for Quote widgets when the live Three.js system isn't in the editor yet — it
  // implements just enough of `StudioSystemType` to drive presentational rendering. Cast through
  // `unknown` because we intentionally don't satisfy the full live-runtime interface (mounting
  // helpers, command execution, etc.).
  return stub as unknown as StudioSystemType
}

/**
 * Resolve the Quote placeholder from design JSON when duplicate ``OsSystem`` siblings exist.
 * Prefer ``systemUuid`` but merge in priced fields from any richer sibling (backend ensure
 * consolidates these on save; this covers the window before that POST returns).
 */
export const resolveQuoteStudioSystemFromDesign = (
  designJson: any,
  systemUuid: string | null | undefined
): StudioSystemType | null => {
  if (!designJson) return null
  const children = designJson?.object?.children
  if (!Array.isArray(children)) return null

  const osNodes = children.filter((c: any) => c?.type === 'OsSystem')
  if (!osNodes.length) return null

  let resolved: StudioSystemType | null = null
  for (const node of osNodes) {
    const uuid = typeof node?.uuid === 'string' ? node.uuid : null
    if (!uuid) continue
    const stub = extractQuoteStudioSystemStubFromDesign(designJson, uuid)
    if (!stub) continue
    if (systemUuid && normUuid(uuid) === normUuid(systemUuid)) {
      resolved = resolved ? pickFresherQuoteStudioSystem(resolved, stub) : stub
    } else if (!resolved) {
      resolved = stub
    } else {
      resolved = pickFresherQuoteStudioSystem(resolved, stub)
    }
  }

  if (systemUuid && resolved) {
    const preferred = extractQuoteStudioSystemStubFromDesign(designJson, systemUuid)
    if (preferred) {
      resolved = pickFresherQuoteStudioSystem(preferred, resolved)
    }
  }

  return resolved
}
